import {
  HORIZONTAL_PAGINATION_THRESHOLD_DAYS,
  HORIZONTAL_WINDOW_EDGE_BUFFER_DAYS,
  HORIZONTAL_WINDOW_OVERSCAN_DAYS,
  MAX_VISIBLE_DATES,
} from '@/modules/planner';

import { VisibleDateRange } from '../../types';

interface PlannerViewportParams {
  scrollLeft: number;
  clientWidth: number;
  cellWidthPx: number;
}

interface HorizontalWindowRangeParams extends PlannerViewportParams {
  datesLength: number;
  previousRange: VisibleDateRange;
}

export interface PlannerViewportIndices {
  startIndex: number;
  endIndex: number;
}

export const getViewportIndices = ({
  scrollLeft,
  clientWidth,
  cellWidthPx,
}: PlannerViewportParams): PlannerViewportIndices => {
  return {
    startIndex: Math.floor(scrollLeft / cellWidthPx),
    endIndex: Math.ceil((scrollLeft + clientWidth) / cellWidthPx) - 1,
  };
};

export const clampDateIndex = (index: number, datesLength: number): number => {
  return Math.max(0, Math.min(index, datesLength - 1));
};

export const getNextVisibleDateRange = ({
  scrollLeft,
  clientWidth,
  cellWidthPx,
  datesLength,
  previousRange,
}: HorizontalWindowRangeParams): VisibleDateRange | null => {
  const viewportIndices = getViewportIndices({
    scrollLeft,
    clientWidth,
    cellWidthPx,
  });

  if (previousRange.startIndex !== 0 || previousRange.endIndex !== 0) {
    const isWithinBufferedWindow =
      viewportIndices.startIndex >= previousRange.startIndex + HORIZONTAL_WINDOW_EDGE_BUFFER_DAYS &&
      viewportIndices.endIndex <= previousRange.endIndex - HORIZONTAL_WINDOW_EDGE_BUFFER_DAYS;

    if (isWithinBufferedWindow) {
      return null;
    }
  }

  let startIndex = Math.max(0, viewportIndices.startIndex - HORIZONTAL_WINDOW_OVERSCAN_DAYS);
  let endIndex = Math.max(
    startIndex,
    Math.min(datesLength - 1, viewportIndices.endIndex + HORIZONTAL_WINDOW_OVERSCAN_DAYS)
  );

  const windowSize = endIndex - startIndex + 1;
  if (windowSize > MAX_VISIBLE_DATES) {
    const excess = windowSize - MAX_VISIBLE_DATES;
    const trimStart = Math.floor(excess / 2);
    const trimEnd = excess - trimStart;

    startIndex += trimStart;
    endIndex -= trimEnd;
  }

  if (previousRange.startIndex === startIndex && previousRange.endIndex === endIndex) {
    return null;
  }

  return {
    startIndex,
    endIndex,
  };
};

export const getDefaultLeftLoadTriggerPx = (cellWidthPx: number): number => {
  return HORIZONTAL_PAGINATION_THRESHOLD_DAYS * cellWidthPx;
};

export const getDefaultRightLoadTriggerPx = ({
  datesLength,
  cellWidthPx,
  clientWidth,
}: {
  datesLength: number;
  cellWidthPx: number;
  clientWidth: number;
}): number => {
  return Math.max(
    0,
    (datesLength - HORIZONTAL_PAGINATION_THRESHOLD_DAYS) * cellWidthPx - clientWidth
  );
};
